import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { playPing } from "@/context/ChatContext";
import { toast } from "sonner";

const SupportContext = createContext(null);

// Live customer-care chat. Customers chat with the support team (Admin + support agents).
// Uses its own WebSocket to /api/ws/chat to receive support_message / support_thread events.
export const SupportProvider = ({ children }) => {
  const { user } = useAuth();
  const isStaff = !!user && (user.role === "admin" || user.support_agent === true);

  const [open, setOpen] = useState(false);          // customer widget open state
  const [myThread, setMyThread] = useState(null);   // customer's own thread meta
  const [threads, setThreads] = useState([]);        // staff inbox list
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const activeIdRef = useRef(null);
  activeIdRef.current = activeId;

  const loadMyThread = useCallback(async () => {
    try {
      const { data } = await api.get("/support/thread/me");
      setMyThread(data.thread);
      if (data.thread) { setActiveId(data.thread.thread_id); setMessages(data.messages || []); }
    } catch { /* not logged in */ }
  }, []);

  const loadThreads = useCallback(async (status = "open") => {
    try {
      const { data } = await api.get(`/support/threads?status=${status}`);
      setThreads(data);
    } catch { /* not staff */ }
  }, []);

  const connect = useCallback(async () => {
    if (!user) return;
    let ticket = "";
    try { const { data } = await api.get("/chat/ws-token"); ticket = data?.token || ""; } catch { /* cookie fallback */ }
    if (!user) return;
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
    const ws = new WebSocket(`${base}/api/ws/chat${ticket ? `?token=${encodeURIComponent(ticket)}` : ""}`);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      let data; try { data = JSON.parse(e.data); } catch { return; }
      if (data.type === "support_message") {
        const msg = data.message;
        if (data.thread_id === activeIdRef.current) {
          setMessages((m) => (m.some((x) => x.message_id === msg.message_id) ? m : [...m, msg]));
        } else if (msg && msg.sender_id !== user.user_id) {
          playPing();
          const who = msg.sender_role === "support" ? "Customer care" : msg.sender_name || "Customer";
          toast(`🎧 ${who}`, { description: (msg.text || "").slice(0, 90) });
        }
      } else if (data.type === "support_thread") {
        const th = data.thread;
        if (th.customer_id === user.user_id) {
          setMyThread((prev) => (th.thread_id === activeIdRef.current ? { ...th, unread: 0 } : th));
        }
        setThreads((prev) => {
          if (!isStaff) return prev;
          const rest = prev.filter((t) => t.thread_id !== th.thread_id);
          const merged = th.thread_id === activeIdRef.current ? { ...th, unread: 0 } : th;
          return [merged, ...rest].sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
        });
      }
    };
    ws.onclose = () => { wsRef.current = null; if (user) reconnectRef.current = setTimeout(connect, 3000); };
  }, [user, isStaff]);

  useEffect(() => {
    if (user) {
      connect();
      if (isStaff) loadThreads("open"); else loadMyThread();
    } else {
      setMyThread(null); setThreads([]); setActiveId(null); setMessages([]); setOpen(false);
    }
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) { const w = wsRef.current; wsRef.current = null; w.close(); }
    };
  }, [user, isStaff, connect, loadThreads, loadMyThread]);

  const ensureThread = useCallback(async () => {
    try {
      await api.post("/support/thread");
      await loadMyThread();
    } catch { /* ignore */ }
  }, [loadMyThread]);

  const openThread = useCallback(async (threadId) => {
    setActiveId(threadId); setMessages([]);
    try {
      const { data } = await api.get(`/support/thread/${threadId}/messages`);
      setMessages(data.messages || []);
      setThreads((prev) => prev.map((t) => (t.thread_id === threadId ? { ...t, unread: 0 } : t)));
    } catch { /* ignore */ }
  }, []);

  const send = useCallback(async (text) => {
    const clean = (text || "").trim();
    if (!clean || !activeIdRef.current) return;
    try {
      const { data } = await api.post(`/support/thread/${activeIdRef.current}/message`, { text: clean });
      setMessages((m) => (m.some((x) => x.message_id === data.message_id) ? m : [...m, data]));
    } catch { /* ignore */ }
  }, []);

  const threadAction = useCallback(async (threadId, action) => {
    try { await api.post(`/support/thread/${threadId}/${action}`); await loadThreads(action === "close" ? "open" : "open"); } catch { /* ignore */ }
  }, [loadThreads]);

  const openWidget = useCallback(async () => { setOpen(true); await ensureThread(); }, [ensureThread]);

  const customerUnread = myThread?.unread || 0;
  const staffUnread = useMemo(() => threads.reduce((s, t) => s + (t.thread_id === activeId ? 0 : (t.unread || 0)), 0), [threads, activeId]);

  const value = useMemo(() => ({
    isStaff, open, setOpen, openWidget,
    myThread, messages, activeId,
    threads, loadThreads, openThread, threadAction,
    ensureThread, send,
    customerUnread, staffUnread,
  }), [isStaff, open, openWidget, myThread, messages, activeId, threads, loadThreads, openThread, threadAction, ensureThread, send, customerUnread, staffUnread]);

  return <SupportContext.Provider value={value}>{children}</SupportContext.Provider>;
};

export const useSupport = () => useContext(SupportContext);
